import { DateRange } from '@robocorp/components';
import { roundToNearestMinutes, startOfMonth, subDays, subHours } from 'date-fns';
import { TimeRangeValues } from '~/constants/timeRanges';
import {
  dateObjectToDateString,
  getEndOfDayInTimezone,
  getStartOfDayInTimezone,
} from './timeHelper';

export type FilterParams = Partial<Record<string, Date>>;

export const getTimeRangeParams = (fieldName: string, dateRange: DateRange) => {
  if (dateRange?.from === undefined && dateRange?.to === undefined) {
    return {};
  }

  if (dateRange?.to === undefined) {
    return {
      [`${fieldName}.gte`]: dateRange.from,
    };
  }
  return {
    [`${fieldName}.gte`]: dateRange.from,
    [`${fieldName}.lte`]: dateRange.to,
  };
};

const getCurrentDate = () => roundToNearestMinutes(new Date());

/**
 * If returns "undefined" for dateRange, it's meant as ALL TIME - shouldn't be anything passes to API requests in date range fields
 */
export const getPresetTimeRanges = ({
  preset,
  customRange,
  billingStart,
  billingEnd,
  timezone,
  maxFrom,
}: {
  preset: TimeRangeValues;
  customRange?: DateRange;
  billingStart?: Date;
  billingEnd?: Date;
  timezone: string;
  maxFrom?: Date | string;
}) => {
  const timestamp = getCurrentDate();
  const endDate = getEndOfDayInTimezone(timestamp, timezone);

  switch (preset) {
    case TimeRangeValues.LAST_24_HOURS:
      return { range: { from: subHours(endDate, 24) }, timestamp };
    case TimeRangeValues.LAST_7_DAYS:
      return { range: { from: getStartOfDayInTimezone(subDays(endDate, 7), timezone) }, timestamp };
    case TimeRangeValues.LAST_30_DAYS:
      return {
        range: { from: getStartOfDayInTimezone(subDays(endDate, 30), timezone) },
        timestamp,
      };
    case TimeRangeValues.THIS_MONTH:
      return {
        range: { from: getStartOfDayInTimezone(startOfMonth(endDate), timezone) },
        timestamp,
      };
    case TimeRangeValues.CURRENT_BILLING_PERIOD:
      if (billingStart !== undefined && billingEnd !== undefined) {
        return {
          range: {
            from: getStartOfDayInTimezone(billingStart, timezone),
            to: getEndOfDayInTimezone(billingEnd, timezone),
          },
          timestamp,
        };
      }
      return {
        range: { from: getStartOfDayInTimezone(startOfMonth(endDate), timezone) },
        timestamp,
      };
    case TimeRangeValues.CUSTOM:
      if (customRange?.from !== undefined && customRange.to !== undefined) {
        return {
          range: {
            from: getStartOfDayInTimezone(customRange.from, timezone),
            to: getEndOfDayInTimezone(customRange.to, timezone),
          },
          timestamp,
        };
      }
      return { range: customRange, timestamp };

    case TimeRangeValues.ALL_TIME:
      return {
        range: { from: typeof maxFrom === 'string' ? new Date(maxFrom) : maxFrom, to: undefined },
        timestamp,
      };
    default:
      return { range: undefined, timestamp };
  }
};

export const parseFilterTimeRange = ([preset, range]: [
  TimeRangeValues,
  { from?: string; to?: string } | undefined,
]) => {
  if (!range) {
    return { preset: preset as Exclude<TimeRangeValues, TimeRangeValues.CUSTOM> };
  }

  return {
    preset: TimeRangeValues.CUSTOM as const,
    range: {
      from: range.from ? new Date(String(range.from)) : undefined,
      to: range.to ? new Date(String(range.to)) : undefined,
    },
  };
};

export const stringifyFilterTimeRange = (
  [preset, range]: [TimeRangeValues?, DateRange?, ...unknown[]],
  defaultTimeRange?: TimeRangeValues,
):
  | [TimeRangeValues]
  | [TimeRangeValues.CUSTOM, { from: string | undefined; to: string | undefined }]
  | []
  | undefined => {
  if (!preset || preset === defaultTimeRange) {
    return undefined;
  }

  if (!range) {
    return [preset as Exclude<TimeRangeValues, TimeRangeValues.CUSTOM>];
  }

  return [
    TimeRangeValues.CUSTOM,
    {
      from: range.from ? dateObjectToDateString(range.from) : undefined,
      to: range.to ? dateObjectToDateString(range.to) : undefined,
    },
  ];
};
